import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Moon, Sun, Upload, LogOut, LayoutDashboard, User, Shield } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/explore", label: "Explore" },
  { to: "/creators", label: "Creators" },
  { to: "/categories", label: "Categories" },
  { to: "/about", label: "About" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group" data-testid="brand-logo">
            <div className="w-8 h-8 rounded-md accent-grad grid place-items-center text-white font-black text-sm">C</div>
            <span className="font-display font-bold tracking-tight text-base sm:text-lg">CREATIVE HUB</span>
          </Link>
          <nav className="hidden md:flex items-center gap-7">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors ${isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`
                }
                data-testid={`nav-${n.label.toLowerCase()}`}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle" aria-label="Theme toggle">
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            {user ? (
              <>
                <Button
                  onClick={() => navigate("/dashboard/create")}
                  className="hidden sm:inline-flex gap-2 bg-[hsl(var(--accent))] hover:bg-[hsl(15_100%_55%)] text-white"
                  data-testid="upload-cta"
                >
                  <Upload className="h-4 w-4" /> Upload
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="focus:outline-none" data-testid="user-menu">
                      <Avatar className="h-9 w-9 ring-1 ring-border">
                        <AvatarImage src={user.avatar_url} alt={user.username} />
                        <AvatarFallback>{user.full_name?.[0] || user.username?.[0] || "U"}</AvatarFallback>
                      </Avatar>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>
                      <div className="font-semibold">{user.full_name}</div>
                      <div className="text-xs text-muted-foreground">@{user.username}</div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate(`/u/${user.username}`)} data-testid="menu-profile">
                      <User className="h-4 w-4 mr-2" /> My Portfolio
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">
                      <LayoutDashboard className="h-4 w-4 mr-2" /> Dashboard
                    </DropdownMenuItem>
                    {user.role === "admin" && (
                      <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                        <Shield className="h-4 w-4 mr-2" /> Admin
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="menu-logout">
                      <LogOut className="h-4 w-4 mr-2" /> Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Button variant="ghost" onClick={() => navigate("/login")} data-testid="nav-login" className="text-sm">Login</Button>
                <Button
                  onClick={() => navigate("/signup")}
                  className="bg-[hsl(var(--accent))] hover:bg-[hsl(15_100%_55%)] text-white"
                  data-testid="nav-signup"
                >
                  Sign up
                </Button>
              </>
            )}
          </div>
        </div>
      </header>
      <main>{children}</main>
      <footer className="border-t border-border/60 mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded accent-grad grid place-items-center text-white font-black text-xs">C</div>
            <span className="font-display font-semibold">CREATIVE HUB</span>
          </div>
          <div className="meta-label">© 2026 — Your creativity deserves a space.</div>
        </div>
      </footer>
      <Toaster position="top-right" />
    </div>
  );
}
